import type { RoadmapMilestone } from '@wtta/scoring'

interface RoadmapSectionProps {
  milestones: RoadmapMilestone[]
  /** Reference date used to mark milestones as past, current or upcoming. */
  now: Date
}

type MilestoneStatus = 'past' | 'current' | 'upcoming'

const STATUS_LABEL: Record<MilestoneStatus, string> = {
  past: 'achter de rug',
  current: 'nu actueel',
  upcoming: 'gepland',
}

export function RoadmapSection({ milestones, now }: RoadmapSectionProps) {
  if (milestones.length === 0) return null

  return (
    <section className="report-section roadmap" data-testid="roadmap-section">
      <h2 className="report-section__title">Routekaart richting 1 januari 2027</h2>
      <p className="report-section__lead">
        De Wtta treedt op 1 januari 2027 in werking. Onderstaande momenten
        bepalen de planning, met de aanmeldingsdeadline van 31 december 2026
        als hardste gate.
      </p>

      <ol className="roadmap__list">
        {milestones.map((milestone) => {
          const status = computeStatus(milestone, now)
          return (
            <li
              key={milestone.id}
              className={`roadmap__item roadmap__item--${status}${
                milestone.isCritical ? ' roadmap__item--critical' : ''
              }`}
              data-testid={`milestone-${milestone.id}`}
            >
              <header className="roadmap__item-header">
                <span className="roadmap__date">{milestone.dateLabel}</span>
                <span className={`roadmap__status roadmap__status--${status}`}>
                  {STATUS_LABEL[status]}
                </span>
                {milestone.isCritical ? (
                  <span className="roadmap__critical-flag">harde deadline</span>
                ) : null}
              </header>
              <h3 className="roadmap__title">{milestone.title}</h3>
              <p className="roadmap__description">{milestone.description}</p>
            </li>
          )
        })}
      </ol>
    </section>
  )
}

function computeStatus(milestone: RoadmapMilestone, now: Date): MilestoneStatus {
  const start = new Date(milestone.startDate).getTime()
  const end = milestone.endDate
    ? new Date(milestone.endDate).getTime()
    : start
  const nowMs = now.getTime()

  if (nowMs > end) return 'past'
  if (nowMs >= start && nowMs <= end) return 'current'
  return 'upcoming'
}
